import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Icon } from "@iconify/react";
import { IMAGES } from "@/assets/images";
import { useLang } from "@/i18n";

const advantageIcons = ["mdi:target", "mdi:lightning-bolt-circle", "mdi:chart-line", "mdi:handshake"];

const platforms = [
  { name: "TikTok", icon: "ic:baseline-tiktok", color: "#010101" },
  { name: "Instagram", icon: "mdi:instagram", color: "#E1306C" },
  { name: "YouTube", icon: "mdi:youtube", color: "#FF0000" },
  { name: "Facebook", icon: "mdi:facebook", color: "#1877F2" },
  { name: "Twitter/X", icon: "mdi:twitter", color: "#1DA1F2" },
  { name: "Snapchat", icon: "mdi:snapchat", color: "#FFFC00" },
];

const ease = [0.4, 0, 0.2, 1];

export default function About() {
  const { t } = useLang();
  const advantages = t.about.advantages.map((a, i: number) => ({ ...a, icon: advantageIcons[i] }));
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="about" ref={sectionRef} style={{ background: "#050505", overflow: "hidden" }}>

      {/* ── Top: Full-width cinematic image ── */}
      <div style={{ position: "relative", height: "55vw", minHeight: 240, maxHeight: 620, overflow: "hidden" }}>
        <motion.img
          src={IMAGES.ABOUT_DARK_3}
          alt="About Ascend"
          style={{ width: "100%", height: "115%", objectFit: "cover", objectPosition: "center 30%", y: imgY }}
        />
        {/* Overlays */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(5,5,5,0.88) 0%, rgba(5,5,5,0.45) 55%, rgba(5,5,5,0.1) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(5,5,5,0.5) 0%, transparent 40%, rgba(5,5,5,0.9) 100%)" }} />

        {/* Floating text on image */}
        <div style={{ position: "absolute", bottom: "2.5rem", left: "4rem", right: "4rem", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, ease }}
          >
            <div style={{ fontSize: "0.7rem", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <div style={{ width: 30, height: 1, background: "#f4440c" }} />
              {t.about.eyebrow}
            </div>
            <h2 style={{
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: "clamp(2.8rem, 5.5vw, 5.5rem)",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 0.95,
              letterSpacing: "-0.04em",
            }}>
              {t.about.headline1}
            </h2>
            <p style={{ fontSize: "clamp(2.8rem, 5.5vw, 5.5rem)", fontWeight: 700, lineHeight: 0.95, letterSpacing: "-0.04em", color: "transparent", WebkitTextStroke: "1.5px rgba(255,255,255,0.25)" }}>
              We Are Ascend
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.4, ease }}
            style={{ textAlign: "right" }}
          >
            <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "5rem", fontWeight: 300, color: "rgba(244,68,12,0.25)", lineHeight: 1 }}>2022</span>
            <div style={{ fontSize: "0.7rem", letterSpacing: "0.14em", color: "rgba(255,255,255,0.3)", textTransform: "uppercase" }}>Est. Shenzhen</div>
          </motion.div>
        </div>
      </div>

      {/* ── Body content ── */}
      <div className="max-w-7xl mx-auto about-body-pad">

        {/* Intro paragraph — large text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease }}
          style={{ maxWidth: 780, marginBottom: "5rem" }}
        >
          <p style={{
            fontSize: "clamp(1.15rem, 2vw, 1.5rem)",
            lineHeight: 1.85,
            color: "rgba(255,255,255,0.72)",
            fontWeight: 300,
            letterSpacing: "0.01em",
          }}>
            {t.about.body}
          </p>
        </motion.div>

        {/* 4-advantage grid */}
        <div className="adv-grid" style={{ marginBottom: "5rem", background: "rgba(255,255,255,0.06)" }}>
          {advantages.map((a, i) => (
            <motion.div
              key={a.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08, ease }}
              whileHover={{ background: "rgba(244,68,12,0.06)" }}
              style={{
                background: "#050505",
                padding: "2.8rem 2.2rem",
                cursor: "default",
                transition: "background 0.3s",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.8rem" }}>
                <span style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: "2.5rem", fontWeight: 300, color: "rgba(244,68,12,0.3)", lineHeight: 1 }}>{a.num}</span>
                <Icon icon={a.icon} style={{ fontSize: 22, color: "#f4440c" }} />
              </div>
              <h3 style={{ fontSize: "1rem", fontWeight: 600, color: "#fff", letterSpacing: "0.04em", marginBottom: "1rem" }}>{a.label}</h3>
              <p style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.9, fontWeight: 300 }}>{a.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Platforms */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "2rem" }}>
            <div style={{ width: 28, height: 1, background: "#f4440c" }} />
            <span style={{ fontSize: "0.7rem", letterSpacing: "0.18em", color: "rgba(255,255,255,0.35)", textTransform: "uppercase" }}>
              {t.about.platforms}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "2.5rem", flexWrap: "wrap" }}>
            {platforms.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06, ease }}
                whileHover={{ scale: 1.1 }}
                style={{ display: "flex", alignItems: "center", gap: "0.6rem", cursor: "default" }}
              >
                <Icon icon={p.icon} style={{ fontSize: 20, color: p.color === "#010101" ? "#fff" : p.color }} />
                <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.45)", letterSpacing: "0.04em", fontWeight: 400 }}>{p.name}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
